import { toJS } from 'mobx';

import { IUserState, UserStatusKey } from '~api/RampPgApiSdk';
import { genericErrorMessage, partialSignUpGenericErrorMessage } from '~constants/Constants';
import { SignUpType } from '~constants/SignUpTypeEnum';
import { IUserFormValues } from '~domain/entities/UserFormStore';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { IMessage, MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';
import { UserStateResolverService } from '~services/UserStateResolverService';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import {
    emitAnalyticsEvent,
    eventTag,
    GetSignUpEventDataParameterType,
    SignUpTaggingEventType,
    TaggingEventSourceType
} from '~utils/Analytics';

import { SignUpSuccessUseCase } from './SignUpSuccessUseCase';
import { SocialConsentSubmittedUseCase } from './social/SocialConsentSubmittedUseCase';
import { UserAlreadyRegisteredUseCase } from './UserAlreadyRegisteredUseCase';

/** Sign-up from in-store (not include online sign-up), social profile, or brand.com */
export class SignUpSubmittedUseCase {
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private type: SignUpType;
    private messageRepository: MessageRepository;
    private resolverService: UserStateResolverService;

    // When user is being redirected, should keep form loading in case user will submit the form again
    private shouldKeepSignUpFormLoading: boolean = false;

    constructor(type: SignUpType) {
        this.type = type;
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.configRepository = diContainer.get(serviceNames.configRepository);
        this.messageRepository = diContainer.get(serviceNames.messageRepository);
    }

    private setErrorMessages = (messages: IMessage[]) =>
        this.messageRepository.setMessages(MessageGroup.SIGN_UP, messages);

    private getEventData = ({ userId, type: failureType }: GetSignUpEventDataParameterType) => {
        let type: SignUpTaggingEventType;
        const { signUpEventSource } = this.userRepository;
        if (failureType) {
            type = failureType;
        } else {
            if (
                // HACK: <SignUp /> view for social-sign-up is not configured with proper SignUpType
                [TaggingEventSourceType.KAKAO_GIFT, TaggingEventSourceType.SSG].includes(
                    signUpEventSource
                ) ||
                this.type === SignUpType.COMPLETE
            ) {
                type = SignUpTaggingEventType.COMPLETE;
            } else {
                type = SignUpTaggingEventType.PARTIAL;
            }
        }

        return {
            source: signUpEventSource,
            type,
            provider: 'standard',
            ...(userId ? { userId } : null)
        };
    };

    private updateUser = async (userId: number, values: IUserFormValues) => {
        // Directly send a request to update user profile
        const updateUserResponse = await this.userRepository.updateUser(userId, values);
        const {
            user: updatedUser,
            posFrontiersErrorKey,
            posIKalarCallbackUrl
        } = updateUserResponse;
        if (
            updatedUser &&
            [
                // partial online user turned to partial offline user from in-store signup form
                UserStatusKey.EXISTING_PARTIAL_OFFLINE,
                // core site partial online user updated
                UserStatusKey.EXISTING_PARTIAL,
                // partial online user turned to complete online user from registration page
                UserStatusKey.EXISTING_COMPLETE,
                // partial offline user turned to complete offline user from registration page
                UserStatusKey.EXISTING_COMPLETE_OFFLINE
            ].includes(updatedUser.userStatusKey)
        ) {
            this.shouldKeepSignUpFormLoading = true;

            emitAnalyticsEvent(eventTag.SIGN_UP_SUCCESSFUL, this.getEventData({ userId }));

            new SignUpSuccessUseCase({
                user: updatedUser,
                posFrontiersErrorKey,
                posIKalarCallbackUrl
            }).execute();
        } else {
            this.messageRepository.setMessages(MessageGroup.SIGN_UP, [
                { transKey: partialSignUpGenericErrorMessage }
            ]);
            emitAnalyticsEvent(
                eventTag.SIGN_UP_FAILED,
                this.getEventData({ type: SignUpTaggingEventType.API_ERROR_OR_IP_LOCK })
            );
        }
    };

    private handleUserAlreadyRegistered = () => {
        emitAnalyticsEvent(
            eventTag.SIGN_UP_FAILED,
            this.getEventData({ type: SignUpTaggingEventType.USER_EXISTING })
        );
        new UserAlreadyRegisteredUseCase(this.resolverService.users as IUserState[]).execute();
    };

    public execute = async () => {
        const {
            isSocialProfile,
            emailIdentifierFieldName,
            iamConfig: { features } = {}
        } = this.configRepository;
        const {
            userFormStore: { setSubmitting, values },
            clearPosFrontierError,
            signUp
        } = this.userRepository;
        const { clearMessagesByGroup } = this.messageRepository;

        setSubmitting(true);
        clearMessagesByGroup(MessageGroup.SIGN_UP);
        clearPosFrontierError();

        if (isSocialProfile) {
            return new SocialConsentSubmittedUseCase().execute();
        }

        const formValues = toJS(values);
        const isMobileOnlyRegistration =
            emailIdentifierFieldName && !formValues[emailIdentifierFieldName];
        const { users, posFrontiersErrorKey, posIKalarCallbackUrl } = await signUp(formValues);
        const genericErrorKey =
            this.type === SignUpType.PARTIAL
                ? partialSignUpGenericErrorMessage
                : genericErrorMessage;
        this.resolverService = new UserStateResolverService(users);

        const mobileOnlyInPartialSignup =
            isMobileOnlyRegistration && this.type === SignUpType.PARTIAL;

        // API error
        if (!users?.length) {
            this.setErrorMessages([{ transKey: genericErrorKey }]);
            emitAnalyticsEvent(
                eventTag.SIGN_UP_FAILED,
                this.getEventData({ type: SignUpTaggingEventType.API_ERROR_OR_IP_LOCK })
            );
        } else if (
            this.resolverService.isMultipleAccountsFound ||
            this.resolverService.isCompleteUserFound ||
            users.some((user) => user.identifierConflict)
        ) {
            this.handleUserAlreadyRegistered();
        } else {
            const user = users[0];
            const { userId, userStatusKey } = user;

            if (
                [
                    UserStatusKey.NEW_COMPLETE,
                    UserStatusKey.NEW_PARTIAL,
                    UserStatusKey.NEW_PARTIAL_OFFLINE
                ].includes(userStatusKey)
            ) {
                // Keep submitting status to disable submit button for IAM sign-in form
                this.shouldKeepSignUpFormLoading = userStatusKey === UserStatusKey.NEW_COMPLETE;
                emitAnalyticsEvent(eventTag.SIGN_UP_SUCCESSFUL, this.getEventData({ userId }));
                new SignUpSuccessUseCase({
                    user,
                    posFrontiersErrorKey,
                    posIKalarCallbackUrl,
                    isMobileOnlyRegistration
                }).execute();
            } else if (
                userStatusKey === UserStatusKey.EXISTING_PARTIAL_OFFLINE &&
                (!features?.allowPartialOfflineUserEdit || mobileOnlyInPartialSignup)
            ) {
                this.handleUserAlreadyRegistered();
            } else {
                await this.updateUser(userId, toJS(values));
            }
        }

        if (!this.shouldKeepSignUpFormLoading) {
            setSubmitting(false);
        }
    };
}
